import { extendObservable, reaction } from 'mobx';
import { auth } from './auth';
import { api } from './api';
import ApiUtil from '../utils/api';
import StaticData from '../utils/staticData.json';

class Store {
  constructor() {
    extendObservable(this, {
      user: {
        token: auth.getUserToken(),
        groups: [],
        isLoading: true,
      },
      page: {
        color: '',
      },
      developments: this.getDefaultData(),
      buildings: this.getDefaultData(),
      apartments: this.getDefaultData(),
      managingAgents: this.getDefaultData(),
      tariffs: this.getDefaultData(),
      tariffPeriods: this.getDefaultData(),
      managementAgreements: this.getDefaultData(),
      meters: this.getDefaultData(),
      readings: this.getDefaultData(),
      quarantines: this.getDefaultData(),
      calibrations: this.getDefaultData(),
      meterCommissions: this.getDefaultData(),
      lessees: this.getDefaultData(),
      occupants: this.getDefaultData(),
      leases: this.getDefaultData(),
      subleases: this.getDefaultData(),
      bills: this.getDefaultData(),
      creditNotes: this.getDefaultData(),
      billingApprovalReports: this.getDefaultData(),
      periodicBillsDue: this.getDefaultData(),
      propertyTypes: {
        data: this.getPropertyTypes(),
        selected: {},
      },
      meterTypes: {
        data: this.getMeterTypes(),
        selected: {},
      },
      localDistributionZones: {
        data: this.getLocalDistributionZones(),
        selected: {},
      }
    });
  }

  getDefaultData() {
    return {
      num_pages: 1,
      data: [],
      selected: {},
    };
  }

  changeColor(key) {
    this.page.color = key;
  }

  async loadPropertyManagement(query, type) {
    const { num_pages, results } = await ApiUtil.propertyManagementAPI({ query });

    this[type].data = results;
    this[type].num_pages = num_pages;
  }

  async loadTariffs(params = {}) {
    params = Object.assign(
      { page: '1', all: '1', search: '', active_only: !!params.filter },
      params,
    );
    const query = `tariffs/?${this._buildQueryParams(params)}`;
    const { num_pages, results } = await ApiUtil.tariffManagementAPI({ query });

    this.tariffs.data = results;
    this.tariffs.num_pages = num_pages;
  }

  async loadTariffPeriods(params = {}) {
    params = Object.assign(
      { page: '1', all: '1', search: '', active_only: !!params.filter },
      params,
    );
    const query = `tariff_periods/?${this._buildQueryParams(params)}`;
    const { num_pages, results } = await ApiUtil.tariffManagementAPI({ query });

    this.tariffPeriods.data = results;
    this.tariffPeriods.num_pages = num_pages;
  }

  async loadBilling(query, type) {
    const { num_pages, results } = await ApiUtil.billingAPI({ query });

    this[type].data = results;
    this[type].num_pages = num_pages;
  }

  async loadDevelopments(params) {
    params = Object.assign({ page: '1', all: '1' }, params);
    const query = `developments/?${this._buildQueryParams(params)}`;
    await this.loadPropertyManagement(query, 'developments');
  }

  async loadBuildings(params) {
    params = Object.assign({ page: '1', all: '1' }, params);
    const query = `buildings/?${this._buildQueryParams(params)}`;
    await this.loadPropertyManagement(query, 'buildings');
  }

  async loadApartments(params) {
    params = Object.assign({ page: '1', all: '1' }, params);
    const query = `apartments/?${this._buildQueryParams(params)}`;
    await this.loadPropertyManagement(query, 'apartments');
  }

  async loadManagingAgents(params) {
    params = Object.assign({ page: '1', all: '1' }, params);
    const query = `managingagents/?${this._buildQueryParams(params)}`;
    await this.loadPropertyManagement(query, 'managingAgents');
  }

  async loadLessees(params) {
    params = Object.assign({ page: '1', all: '1' }, params);
    const query = `lessees/?${this._buildQueryParams(params)}`;
    await this.loadPropertyManagement(query, 'lessees');
  }

  async loadOccupants(params) {
    params = Object.assign({ page: '1', all: '1' }, params);
    const query = `occupants/?${this._buildQueryParams(params)}`;
    await this.loadPropertyManagement(query, 'occupants');
  }

  async loadManagementAgreements(params) {
    params = Object.assign({ page: '1', all: '1', active_only: !!params.filter }, params);
    const query = `managementagreements/?${this._buildQueryParams(params)}`;
    await this.loadPropertyManagement(query, 'managementAgreements');
  }

  async loadLeases(params) {
    params = Object.assign({ page: '1', all: '1', active_only: !!params.filter }, params);
    const query = `leases/?${this._buildQueryParams(params)}`;
    await this.loadPropertyManagement(query, 'leases');
  }

  async loadSubLeases(params) {
    params = Object.assign({ page: '1', all: '1', active_only: !!params.filter }, params);
    const query = `subleases/?${this._buildQueryParams(params)}`;
    await this.loadPropertyManagement(query, 'subleases');
  }

  async loadMeters(params) {
    params = Object.assign({ page: '1', all: '1' }, params);
    const query = `meters/?${this._buildQueryParams(params)}`;
    await this.loadPropertyManagement(query, 'meters');
  }

  async loadReadings(params) {
    params = Object.assign({ page: '1', all: '1' }, params);
    const query = `readings/?${this._buildQueryParams(params)}`;
    await this.loadPropertyManagement(query, 'readings');
  }

  async loadQuarantines(params) {
    params = Object.assign({ page: '1', all: '1' }, params);
    const query = `quarantines/?${this._buildQueryParams(params)}`;
    await this.loadPropertyManagement(query, 'quarantines');
  }

  async loadCalibrations(params) {
    params = Object.assign({ page: '1', all: '1' }, params);
    const query = `calibrations/?${this._buildQueryParams(params)}`;
    await this.loadPropertyManagement(query, 'calibrations');
  }

  async loadMeterCommissions(params) {
    params = Object.assign({ page: '1', all: '1' }, params);
    const query = `metercommissions/?${this._buildQueryParams(params)}`;
    await this.loadPropertyManagement(query, 'meterCommissions');
  }

  async loadBills(params) {
    params = Object.assign({ page: '1', all: '1' }, params);
    const query = `bills/?${this._buildQueryParams(params)}`;
    await this.loadBilling(query, 'bills');
  }

  async loadPeriodicBillsDue(params) {
    const query = 'get_properties_ready_for_billing';
    await this.loadBilling(query, 'periodicBillsDue');
  }

  async loadCreditNotes(params) {
    params = Object.assign({ page: '1', all: '1' }, params);
    const query = `credit_notes/?${this._buildQueryParams(params)}`;
    await this.loadBilling(query, 'creditNotes');
  }

  async loadBillingApprovalReports(params) {
    params = Object.assign({ page: '1', all: '1' }, params);
    const query = `bill_approval_reports/?${this._buildQueryParams(params)}`;
    await this.loadBilling(query, 'billingApprovalReports');
  }

  _buildQueryParams(params) {
    return Object.keys(params)
      .map(prop => params[prop] && `${prop}=${params[prop]}`)
      .filter(prop => !!prop)
      .join('&');
  }

  initialize() {
    reaction(
      () => this.user.token,
      userToken => {
        this.user.isLoading = true;
        // Set the user's group to default empty
        this.user.groups = [];
        // Get user's permissions/groups from the server
        api({ method: 'GET', endpoint: '/auth/get_users_groups' }).then(userGroups => {
          // Store the result in mobx store
          this.user.groups = (userGroups && userGroups.groups) || [];
          this.user.isLoading = false;
        });
      },
      { fireImmediately: true },
    );
  }

  getPropertyTypes = () => [
    {
      id: 'development',
      value: 'Development',
    },
    {
      id: 'building',
      value: 'Building',
    },
    {
      id: 'apartment',
      value: 'Apartment',
    },
  ];

  getMeterTypes = () => [
    {
      id: 'CW',
      value: 'Cold water',
    },
    {
      id: 'HW',
      value: 'Hot water',
    },
    {
      id: 'H',
      value: 'Heat',
    },
    {
      id: 'G',
      value: 'Gas',
    },
    {
      id: 'E',
      value: 'Electricity',
    },
    {
      id: 'CHW',
      value: 'Cooling',
    },
    {
      id: 'WW',
      value: 'Waste water',
    },
  ];

  getLocalDistributionZones = () => {
    return StaticData.LOCAL_DISTRIBUTION_ZONES.map(item => {
      return { id: item.key, value: item.value }
    })
  }

  setMeterType = id => {
    this.meterTypes.selected = this.meterTypes.data.find(meter => meter.id === id) || {};
  };

  setPropertyType = id => {
    this.propertyTypes.selected = this.propertyTypes.data.find(prop => prop.id === id) || {};
  };

  setLocalDistributionZone = id => {
    this.localDistributionZones.selected = this.localDistributionZones.data.find(localDistributionZone => localDistributionZone.id === id) || {};
  }
}

const store = new Store();

export default store;



// WEBPACK FOOTER //
// ./src/libs/store.js